import { computed } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';
import type { ClientStore, ShopifyPlan } from '@shopexperts/shared';

export interface CreateStorePayload {
  store_url: string;
  store_name: string;
  shopify_plan?: ShopifyPlan;
}

/**
 * Composable for creating a new store during lead submission.
 * 
 * When a user submits a lead with a new store, we need to create the store
 * first to get a store_id, then submit the lead with that store_id.
 * 
 * @example
 * ```ts
 * const { createStore, isCreating } = useCreateStore();
 * 
 * const store = await createStore({
 *   store_url: 'https://mystore.myshopify.com',
 *   store_name: 'My Store',
 *   shopify_plan: 'basic',
 * });
 * ```
 */
export function useCreateStore() {
  const api = useApi();
  const toaster = useToaster();

  const createStoreMutation = useMutation({
    mutationKey: ['client', 'createStore'],
    mutationFn: async (payload: CreateStorePayload): Promise<ClientStore> => {
      return await api.client.storesV2.createStore({
        body: {
          url: payload.store_url,
          title: payload.store_name,
          shopify_plan: payload.shopify_plan,
        },
      });
    },
    onError: (error: any) => {
      console.error('[useCreateStore] Error:', error);
      
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.message ||
        'Failed to create store. Please try again.';
      
      toaster.create({
        title: 'Store creation failed',
        description: errorMessage,
        type: 'error',
      });
    },
  });

  const isCreating = computed(() => createStoreMutation.isPending.value);
  const isSuccess = computed(() => createStoreMutation.isSuccess.value);
  const isError = computed(() => createStoreMutation.isError.value);
  const error = computed(() => createStoreMutation.error.value);

  const createStore = (payload: CreateStorePayload) => {
    return createStoreMutation.mutateAsync(payload);
  };

  return {
    createStoreMutation,
    isCreating,
    isSuccess,
    isError,
    error,
    createStore,
  };
}
